import React from 'react';

const Section = ({ title, content }) => {
    if (!content) return null;
    return (
        <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-justify">
                {content}
            </p>
        </div>
    );
};

const SafetyCard = ({ icon, title, level, description }) => {
    if (!description) return null;
    
    // Determine color based on level string
    let colorClass = "bg-gray-100 text-gray-800";
    let iconColorClass = "text-gray-500";
    
    if (level === 'UNSAFE' || level === 'UNSAFE TO DRIVE') {
        colorClass = "bg-red-50 text-red-700 border-red-200";
        iconColorClass = "text-red-500";
    } else if (level === 'CAUTION' || level === 'CONSULT YOUR DOCTOR') {
        colorClass = "bg-amber-50 text-amber-800 border-amber-200";
        iconColorClass = "text-amber-500";
    } else if (level === 'SAFE') {
        colorClass = "bg-emerald-50 text-emerald-800 border-emerald-200";
        iconColorClass = "text-emerald-500";
    } else {
        colorClass = "bg-gray-50 text-gray-800 border-gray-200";
    }

    return (
        <div className={`rounded-2xl border p-5 flex flex-col h-full ${colorClass}`}>
            <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-full bg-white bg-opacity-60 ${iconColorClass}`}>
                    <i className={`fas ${icon} text-lg`}></i>
                </div>
                <div>
                    <h4 className="font-bold text-sm uppercase tracking-wide">{title}</h4>
                    <span className="text-xs font-bold opacity-80">{level}</span>
                </div>
            </div>
            <p className="text-sm opacity-90 leading-relaxed mt-2 whitespace-pre-line">
                {description}
            </p>
        </div>
    );
};

const RichMedicalData = ({
    introduction,
    primaryUse,
    benefits,
    sideEffects,
    howToUse,
    howItWorks,
    alcoholInteraction,
    pregnancyInteraction,
    lactationInteraction,
    drivingInteraction,
    kidneyInteraction,
    liverInteraction,
    fallbackDescription,
    fallbackIndications
}) => {
    
    // Only show if we have some form of medical data
    const hasData = introduction || primaryUse || benefits || sideEffects || howToUse || howItWorks || fallbackDescription || fallbackIndications;
    if (!hasData) return null;

    // Helper to extract the first caps word as a level indicator from safety advice (e.g. "CAUTION: Use with care")
    const extractLevelAndDesc = (text) => {
        if (!text) return { level: '', description: '' };
        
        // Match exact strings the AI might use based on 1mg
        const match = text.match(/^(UNSAFE TO DRIVE|UNSAFE|CAUTION|CONSULT YOUR DOCTOR|SAFE)\b[:\-\.]*\s*/i);
        if (match) {
            return {
                level: match[1].toUpperCase(),
                description: text.substring(match[0].length).trim()
            };
        }
        
        // If no explicit level matched at the start, just return the text
        return { level: '', description: text };
    };

    const alcohol = extractLevelAndDesc(alcoholInteraction);
    const pregnancy = extractLevelAndDesc(pregnancyInteraction);
    const lactation = extractLevelAndDesc(lactationInteraction);
    const driving = extractLevelAndDesc(drivingInteraction);
    const kidney = extractLevelAndDesc(kidneyInteraction);
    const liver = extractLevelAndDesc(liverInteraction);

    const hasSafetyAdvice = alcohol.description || pregnancy.description || lactation.description || driving.description || kidney.description || liver.description;

    return (
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-12 border border-gray-100 p-6 sm:p-10">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-8 border-b pb-4">Product Details</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* Left Column: General Medical Info */}
                <div className="lg:col-span-2 space-y-2">
                    <Section title="Product Introduction" content={introduction || fallbackDescription} />
                    <Section title="Primary Uses" content={primaryUse || fallbackIndications} />
                    <Section title="Benefits" content={benefits} />
                    <Section title="Side Effects" content={sideEffects} />
                    <Section title="How to Use" content={howToUse} />
                    <Section title="How It Works" content={howItWorks} />
                </div>

                {/* Right Column: Safety Advice */}
                {hasSafetyAdvice && (
                    <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-gray-100 pt-8 lg:pt-0 lg:pl-10">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <i className="fas fa-shield-alt text-sky-500"></i>
                            Safety Advice
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                            <SafetyCard icon="fa-wine-glass-alt" title="Alcohol" level={alcohol.level} description={alcohol.description} />
                            <SafetyCard icon="fa-baby" title="Pregnancy" level={pregnancy.level} description={pregnancy.description} />
                            <SafetyCard icon="fa-baby-carriage" title="Breastfeeding" level={lactation.level} description={lactation.description} />
                            <SafetyCard icon="fa-car" title="Driving" level={driving.level} description={driving.description} />
                            <SafetyCard icon="fa-capsules" title="Kidney" level={kidney.level} description={kidney.description} />
                            <SafetyCard icon="fa-heartbeat" title="Liver" level={liver.level} description={liver.description} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RichMedicalData;
